"use client";

/**
 * Standalone browser-side SSE transport for Studio web apps.
 *
 * This implementation intentionally avoids importing the shared runtime
 * package so the web package can build independently in monorepo setups
 * where the shared runtime path is unavailable or not transpiled.
 */

export interface StreamTransportEvent {
  event: string;
  data: unknown;
  id?: string;
  retry?: number;
}

export interface StreamTransport {
  stream(
    operationId: string,
    payload: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ): AsyncIterable<StreamTransportEvent>;
}

export interface FetchStreamTransportOptions {
  baseUrl?: string;
  defaultHeaders?: () => Record<string, string>;
}

interface ParsedSseEvent {
  event: string;
  data: string;
  id?: string;
  retry?: number;
}

interface SseParser {
  feed(chunk: string): readonly ParsedSseEvent[];
  flush(): readonly ParsedSseEvent[];
}

function createSseParser(): SseParser {
  let buffer = "";

  const parseFrame = (frame: string): ParsedSseEvent | null => {
    if (!frame.trim()) return null;

    let event = "message";
    let data = "";
    let id: string | undefined;
    let retry: number | undefined;

    for (const rawLine of frame.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (!line || line.startsWith(":")) continue;

      const colonIndex = line.indexOf(":");
      const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
      const value =
        colonIndex === -1 ? "" : line.slice(colonIndex + 1).replace(/^ /, "");

      switch (field) {
        case "event":
          event = value || "message";
          break;
        case "data":
          data = data ? `${data}\n${value}` : value;
          break;
        case "id":
          id = value;
          break;
        case "retry": {
          const parsed = Number(value);
          if (Number.isFinite(parsed)) retry = parsed;
          break;
        }
        default:
          break;
      }
    }

    return {
      event,
      data,
      ...(id !== undefined ? { id } : {}),
      ...(retry !== undefined ? { retry } : {}),
    };
  };

  return {
    feed(chunk) {
      buffer += chunk;
      buffer = buffer.replace(/\r\n/g, "\n");
      const events: ParsedSseEvent[] = [];
      let boundary = buffer.indexOf("\n\n");

      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(frame);
        if (parsed) events.push(parsed);
        boundary = buffer.indexOf("\n\n");
      }

      return events;
    },
    flush() {
      if (!buffer.trim()) {
        buffer = "";
        return [];
      }
      const parsed = parseFrame(buffer);
      buffer = "";
      return parsed ? [parsed] : [];
    },
  };
}

function decodeSseData(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

function normalizeBaseUrl(baseUrl: string | undefined): string {
  return String(baseUrl ?? "").trim().replace(/\/+$/, "");
}

function buildHeaders(
  defaultHeaders: (() => Record<string, string>) | undefined,
): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    ...(defaultHeaders?.() ?? {}),
  };
}

function toErrorPayload(message: string, status?: number): Record<string, unknown> {
  return { message, ...(typeof status === "number" ? { status } : {}) };
}

export function createFetchStreamTransport(
  options: FetchStreamTransportOptions = {},
): StreamTransport {
  const baseUrl = normalizeBaseUrl(options.baseUrl);

  return {
    async *stream(operationId, payload, opts) {
      const url = `${baseUrl}/api/operations/${encodeURIComponent(operationId)}`;
      const init: RequestInit = {
        method: "POST",
        headers: buildHeaders(options.defaultHeaders),
        body: JSON.stringify(payload ?? {}),
        credentials: "include",
      };

      if (opts?.signal) init.signal = opts.signal;

      try {
        const response = await fetch(url, init);
        if (!response.ok || !response.body) {
          const text = await response.text().catch(() => "");
          yield {
            event: "error",
            data: toErrorPayload(text || `Stream failed: ${response.status}`, response.status),
          };
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        const parser = createSseParser();

        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            for (const evt of parser.feed(decoder.decode(value, { stream: true }))) {
              yield {
                event: evt.event,
                data: decodeSseData(evt.data),
                ...(evt.id !== undefined ? { id: evt.id } : {}),
                ...(evt.retry !== undefined ? { retry: evt.retry } : {}),
              };
            }
          }

          const trailing = decoder.decode();
          if (trailing) {
            for (const evt of parser.feed(trailing)) {
              yield {
                event: evt.event,
                data: decodeSseData(evt.data),
                ...(evt.id !== undefined ? { id: evt.id } : {}),
                ...(evt.retry !== undefined ? { retry: evt.retry } : {}),
              };
            }
          }

          for (const evt of parser.flush()) {
            yield {
              event: evt.event,
              data: decodeSseData(evt.data),
              ...(evt.id !== undefined ? { id: evt.id } : {}),
              ...(evt.retry !== undefined ? { retry: evt.retry } : {}),
            };
          }
        } finally {
          try {
            reader.releaseLock();
          } catch {
            // Ignore lock release errors; the stream is already complete.
          }
        }
      } catch (err) {
        yield {
          event: "error",
          data: toErrorPayload(err instanceof Error ? err.message : "Stream failed"),
        };
      }
    },
  };
}